import './Pagination.css'

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page)
    }
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, page: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handlePageChange(page)
    }
  }

  // Don't render if there's only one page or invalid input
  if (totalPages <= 0 || currentPage < 1) {
    return null
  }

  // Don't show pagination for single page
  if (totalPages === 1) {
    return null
  }

  // Generate page numbers to display
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = []
    const maxVisible = 5

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Always show first page
      pages.push(1)

      if (currentPage > 3) {
        pages.push('ellipsis')
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('ellipsis')
      }

      // Always show last page
      pages.push(totalPages)
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <nav 
      className="pagination" 
      aria-label="Pagination navigation"
    >
      <ul className="pagination__list">
        {/* First page button */}
        {currentPage > 2 && (
          <li className="pagination__item">
            <button
              className="pagination__button pagination__button--first"
              onClick={() => handlePageChange(1)}
              onKeyDown={(e) => handleKeyDown(e, 1)}
              aria-label="Go to first page"
              disabled={isFirstPage}
            >
              <span className="pagination__icon" aria-hidden="true">«</span>
              <span className="pagination__text">First</span>
            </button>
          </li>
        )}

        {/* Previous page button */}
        <li className="pagination__item">
          <button
            className="pagination__button pagination__button--prev"
            onClick={() => handlePageChange(currentPage - 1)}
            onKeyDown={(e) => handleKeyDown(e, currentPage - 1)}
            aria-label="Go to previous page"
            disabled={isFirstPage}
          >
            <span className="pagination__icon" aria-hidden="true">‹</span>
            <span className="pagination__text">Previous</span>
          </button>
        </li>

        {/* Page numbers */}
        {pageNumbers.map((page, index) => (
          <li key={index} className="pagination__item">
            {page === 'ellipsis' ? (
              <span className="pagination__ellipsis" aria-hidden="true">…</span>
            ) : (
              <button
                className={`pagination__button pagination__button--number ${
                  page === currentPage ? 'pagination__button--active' : ''
                }`}
                onClick={() => handlePageChange(page)}
                onKeyDown={(e) => handleKeyDown(e, page)}
                aria-label={`Go to page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
                disabled={page === currentPage}
              >
                {page}
              </button>
            )}
          </li>
        ))}

        {/* Next page button */}
        <li className="pagination__item">
          <button
            className="pagination__button pagination__button--next"
            onClick={() => handlePageChange(currentPage + 1)}
            onKeyDown={(e) => handleKeyDown(e, currentPage + 1)}
            aria-label="Go to next page"
            disabled={isLastPage}
          >
            <span className="pagination__text">Next</span>
            <span className="pagination__icon" aria-hidden="true">›</span>
          </button>
        </li>

        {/* Last page button */}
        {currentPage < totalPages - 1 && (
          <li className="pagination__item">
            <button
              className="pagination__button pagination__button--last"
              onClick={() => handlePageChange(totalPages)}
              onKeyDown={(e) => handleKeyDown(e, totalPages)}
              aria-label="Go to last page"
              disabled={isLastPage}
            >
              <span className="pagination__text">Last</span>
              <span className="pagination__icon" aria-hidden="true">»</span>
            </button>
          </li>
        )}
      </ul>

      {/* Page info for screen readers */}
      <div className="pagination__info" aria-live="polite">
        <span className="pagination__sr-text">
          Page {currentPage} of {totalPages}
        </span>
      </div>
    </nav>
  )
}

export default Pagination